// hooks/useClients.ts
import { useCallback, useEffect, useState } from "react";
import { useAppStore } from "@/core/store/useAppStore";
import { clientService } from "./Client.service";
import { Client } from "@/types";

export const useClients = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { clients, setClients, selectedClient, setSelectedClient, user } =
    useAppStore();

  const fetchClients = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const fetchedClients = await clientService.getClients(true);
      setClients(fetchedClients);

      // Try to restore previously selected client
      const storedClientId = localStorage.getItem("selectedClientId");
      if (storedClientId) {
        const previousClient = fetchedClients.find(
          (client) => client.id === storedClientId,
        );
        if (previousClient) {
          setSelectedClient(previousClient);
          return;
        }
      }

      // Auto-select first client if none selected
      if (!selectedClient && fetchedClients.length > 0) {
        setSelectedClient(fetchedClients[0]);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch clients",
      );
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Store selected client ID in localStorage
  useEffect(() => {
    if (selectedClient) {
      localStorage.setItem("selectedClientId", selectedClient.id);
    }
  }, [selectedClient]);

  const addClient = (newClient: Client) => {
    setClients([...clients, newClient]);
  };

  return {
    clients,
    selectedClient,
    setSelectedClient,
    isLoading,
    error,
    addClient,
    refetch: fetchClients,
  };
};
